import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Divider
} from '@mui/material';
import { GLEntryPreview } from '../services/glCogsService';
import { formatCurrency } from '../utils/formatters';

interface GLCogsEntryPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  entries: GLEntryPreview[];
  isSubmitting: boolean;
  year: number;
  month: number;
  totalCOGS: number;
  totalWriteOffs: number;
}

const GLCogsEntryPreviewDialog: React.FC<GLCogsEntryPreviewDialogProps> = ({
  open,
  onClose,
  onConfirm,
  entries,
  isSubmitting,
  year,
  month,
  totalCOGS,
  totalWriteOffs
}) => {
  // Calculate totals
  const totalDebits = entries.reduce((sum, entry) => sum + entry.debit, 0);
  const totalCredits = entries.reduce((sum, entry) => sum + entry.credit, 0);
  
  // Get month name
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const monthName = monthNames[month];
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>GL Entry Preview</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1">
            COGS and Write-offs for {monthName} {year}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Total COGS: {formatCurrency(totalCOGS)}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Total Write-offs: {formatCurrency(totalWriteOffs)}
            </Typography>
          </Box>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        <Typography variant="subtitle2" gutterBottom>
          The following GL entries will be posted:
        </Typography>
        
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Account Number</TableCell>
                <TableCell>Account Name</TableCell>
                <TableCell align="right">Debit</TableCell>
                <TableCell align="right">Credit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry, index) => (
                <TableRow key={index}>
                  <TableCell>{entry.accountNumber}</TableCell>
                  <TableCell>{entry.accountName}</TableCell>
                  <TableCell align="right">
                    {entry.debit > 0 ? formatCurrency(entry.debit) : ''}
                  </TableCell>
                  <TableCell align="right">
                    {entry.credit > 0 ? formatCurrency(entry.credit) : ''}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ '& td': { fontWeight: 'bold', borderTop: 1 } }}>
                <TableCell colSpan={2}>Total</TableCell>
                <TableCell align="right">{formatCurrency(totalDebits)}</TableCell>
                <TableCell align="right">{formatCurrency(totalCredits)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        
        {Math.abs(totalDebits - totalCredits) > 0.01 && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            Warning: Debits and credits are not balanced. Please review the entries.
          </Typography>
        )}
        
        <Typography variant="body2" color="text.secondary">
          These entries will update the following accounts:
          <ul>
            <li>Debit to 8100001 (COGS - Store) for sales costs</li>
            <li>Debit to 7300001 (Inventory Write Off) for write-off costs</li>
            <li>Credit to 1300002 (Inventory Balances) for the total amount</li>
          </ul>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color="primary"
          variant="contained"
          disabled={isSubmitting || Math.abs(totalDebits - totalCredits) > 0.01}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isSubmitting ? 'Posting...' : 'Post GL Entries'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GLCogsEntryPreviewDialog;
